import React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { countryOptions, countryName } from "@/lib/countries";

// Combobox con búsqueda + virtualización para el picker de país de pasaporte.
// Antes el dropdown era un Radix Select estándar con ~240 <option> que pintaba
// TODOS los items al abrir (visible lag de apertura en mobile + laptops
// modestas). Ahora:
//   - Search input filtra in-place
//   - react-virtual sólo monta los ~10-12 items visibles del scroll
// Mantiene la misma API que el CountrySelect anterior: { value, onChange,
// className, placeholder, lang } y emite onChange({ target: { value } }) para
// compat con consumidores que esperaban un evento estilo <select>.

const ROW_HEIGHT = 36;
const VIEWPORT_HEIGHT = 320;

function CountrySelect({ value, onChange, className, placeholder, lang }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const parentRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const lng = lang === "en" ? "en" : "es";
  const options = countryOptions(lng);

  // Foco al input cuando abre (un tick después del mount del Portal).
  React.useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  // Reset query al cerrar para no arrastrar el filtro a la próxima apertura.
  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  // Strip diacritics so "Mexico" matches "México" — a Spanish-speaking user
  // typing without accents otherwise got "Sin resultados" (new-client audit).
  const norm = (s) =>
    s
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();

  const filtered = React.useMemo(() => {
    const q = norm(query.trim());
    if (!q) return options;
    return options.filter(
      (opt) => norm(opt.label).includes(q) || String(opt.value).toLowerCase().includes(q)
    );
  }, [options, query]);

  const rowVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 6,
  });

  // The Popover Content (and the scroll parent) mount a tick AFTER `open`
  // flips, so the virtualizer was first created with a null scroll element and
  // renders zero rows — the list looked blank until the user typed (which
  // re-rendered and forced a measure). Re-measure once the parent is attached
  // so the full country list is visible the instant the dropdown opens.
  React.useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => rowVirtualizer.measure());
    return () => cancelAnimationFrame(id);
  }, [open, rowVirtualizer]);

  const handleSelect = React.useCallback(
    (val) => {
      onChange?.({ target: { value: val } });
      setOpen(false);
    },
    [onChange]
  );

  const handleClear = React.useCallback(
    (e) => {
      e.stopPropagation();
      onChange?.({ target: { value: "" } });
    },
    [onChange]
  );

  const displayLabel = value ? countryName(value, lng) : "";

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          className={cn(
            "bg-[#0f172a] border border-slate-700 text-white rounded-xl px-4 py-3 text-sm",
            "focus:border-[#0052FF] focus:ring-0 focus:outline-none h-auto",
            "flex items-center justify-between gap-2 w-full text-left",
            !displayLabel && "text-slate-500",
            className
          )}
        >
          <span className="truncate flex-1">{displayLabel || placeholder || " "}</span>
          {displayLabel ? (
            <span
              role="button"
              tabIndex={-1}
              onClick={handleClear}
              className="text-slate-500 hover:text-slate-300 flex-shrink-0"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
          )}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          side="bottom"
          sideOffset={4}
          collisionPadding={16}
          avoidCollisions={false}
          className={cn(
            "z-[100001] w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)]",
            "rounded-xl border border-slate-700 bg-[#0f172a] text-white shadow-md",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        >
          <div className="p-2 border-b border-slate-800 flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-slate-500" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={lng === "en" ? "Search…" : "Buscar…"}
              className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-400 focus:outline-none"
            />
          </div>
          {filtered.length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-400">
              {lng === "en" ? "No matches" : "Sin resultados"}
            </div>
          ) : (
            <div
              ref={parentRef}
              className="overflow-y-auto p-1"
              style={{ height: Math.min(VIEWPORT_HEIGHT, filtered.length * ROW_HEIGHT + 8) }}
            >
              <div
                style={{
                  height: rowVirtualizer.getTotalSize(),
                  position: "relative",
                  width: "100%",
                }}
              >
                {rowVirtualizer.getVirtualItems().map((vRow) => {
                  const opt = filtered[vRow.index];
                  const selected = opt.value === value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleSelect(opt.value)}
                      className={cn(
                        "absolute left-0 right-0 px-3 text-sm text-left flex items-center gap-2 rounded-lg",
                        "hover:bg-slate-800 focus:bg-slate-800 outline-none",
                        selected ? "text-white" : "text-slate-200"
                      )}
                      style={{
                        top: 0,
                        height: ROW_HEIGHT,
                        transform: `translateY(${vRow.start}px)`,
                      }}
                    >
                      <Check
                        className={cn(
                          "w-3.5 h-3.5 flex-shrink-0",
                          selected ? "text-green-400" : "opacity-0"
                        )}
                      />
                      <span className="truncate">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export default React.memo(CountrySelect);
